// src/pages/AdminPage.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminPage.css";
import SpeechIcon from "../components/SpeechIcon";

import GateIcon from "../components/GateIcon";
import OfflineBanner from "../components/OfflineBanner";
import { useAdmin } from "../context/AdminContext";

import {
  findAdminUserByEmail,
  setAdminRoleData,
  setSuperAdminRoleData,
  humanizeAdminError,
} from "../services/adminDataService";

function AdminPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, adminLoading, refreshAdminClaim } = useAdmin();

  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const [email, setEmail] = useState("");
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const [userResult, setUserResult] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    title: "",
    text: "",
    okText: "OK",
    cancelText: "Abbrechen",
    danger: false,
    onOk: null,
  });

  const [toast, setToast] = useState({ visible: false, text: "" });
  const toastTimerRef = useRef(null);

  const showToast = (text) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);

    setToast({ visible: true, text });

    toastTimerRef.current = setTimeout(() => {
      setToast({ visible: false, text: "" });
    }, 2000);
  };

  const openConfirm = ({ title, text, danger = false, onOk }) => {
    setConfirmModal({
      visible: true,
      title,
      text,
      okText: "OK",
      cancelText: "Abbrechen",
      danger,
      onOk,
    });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({
      ...prev,
      visible: false,
    }));
  };

  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch("https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js", {
          method: "HEAD",
          cache: "no-store",
        });
        setIsOffline(false);
      } catch {
        setIsOffline(true);
      }
    };

    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

  const handleSearch = async () => {
    const mail = email.trim().toLowerCase();

    if (!mail || !mail.includes("@")) {
      openConfirm({
        title: "Hinweis",
        text: "Bitte gib eine gültige E-Mail-Adresse ein.",
        onOk: () => closeConfirm(),
      });
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const normalized = await findAdminUserByEmail(mail);

      if (!normalized.uid) {
        setUserResult(null);
        showToast("Kein Nutzer gefunden");
      } else {
        setUserResult(normalized);
        showToast("Nutzer geladen");
      }
    } catch (error) {
      console.warn("Admin Search Error:", error);
      setUserResult(null);

      openConfirm({
        title: "Fehler",
        text: `Suche fehlgeschlagen: ${humanizeAdminError(error)}`,
        danger: true,
        onOk: () => closeConfirm(),
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async ({ role, value }) => {
    if (!userResult?.uid) return;

    setLoading(true);

    try {
      if (role === "admin") {
        await setAdminRoleData({
          targetUid: userResult.uid,
          enabled: value,
        });
      } else if (role === "superadmin") {
        await setSuperAdminRoleData({
          targetUid: userResult.uid,
          enabled: value,
        });
      }

      const refreshed = await findAdminUserByEmail(userResult.email);
      setUserResult(refreshed);

      await refreshAdminClaim?.();

      showToast("Rechte aktualisiert");
    } catch (error) {
      console.warn("Admin SetRole Error:", error);

      openConfirm({
        title: "Fehler",
        text: `Konnte Rechte nicht ändern: ${humanizeAdminError(error)}`,
        danger: true,
        onOk: () => closeConfirm(),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  if (!adminLoading && !isSuperAdmin) {
    return (
      <div className="AdminPage">
        {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

        <img
          src="/logo-edens-ernte-transparent.png"
          alt="Logo"
          className="browse-logo no-tap-highlight"
          onClick={() => navigate("/")}
        />
        <GateIcon className="login-icon" />
<SpeechIcon className="speech-icon no-tap-highlight" />
<img
  src="/rezept-icon.png"
  alt="Rezepthelfer Icon"
  className="rezept-icon no-tap-highlight"
  onClick={() => navigate("/rezepte")}
/>
<p className="top-claim">Nachhaltigkeit – ganz einfach!</p>

        <div className="admin-container">
          <h2>Kein Zugriff</h2>
          <p>Diese Seite ist nur für Superadmins sichtbar.</p>

          <div className="admin-actions-row single" style={{ marginTop: 12 }}>
            <button className="green-button" onClick={() => navigate("/profil")}>
              Zurück zum Profil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasUser = !!userResult?.uid;
  const isUserAdmin = !!userResult?.claims?.admin;
  const isUserSuperAdmin = !!userResult?.claims?.superadmin;

  return (
    <div className="AdminPage">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo no-tap-highlight"
        onClick={() => navigate("/")}
      />
      <GateIcon className="login-icon" />

      <SpeechIcon className="speech-icon no-tap-highlight" />
      <img
        src="/rezept-icon.png"
        alt="Rezepthelfer Icon"
        className="rezept-icon no-tap-highlight"
        onClick={() => navigate("/rezepte")}
      />

      <p className="top-claim">Nachhaltigkeit – ganz einfach!</p>

      <div className="admin-container">
        <h2>Admin-Verwaltung</h2>
        <hr />

        <div className="admin-search-row">
          <input
            type="email"
            placeholder="Nutzer E-Mail eingeben…"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            disabled={loading}
          />
          <button className="green-button" onClick={handleSearch} disabled={loading}>
            {loading ? "Suche…" : "Suchen"}
          </button>
        </div>

        {searched && loading && (
          <div className="admin-empty">
            <p>Suche läuft…</p>
          </div>
        )}

        {searched && !loading && !hasUser && (
          <div className="admin-empty">
            <p>Kein Nutzer gefunden.</p>
          </div>
        )}

        {hasUser && (
          <div className="admin-user-card">
            <div className="admin-user-line">
              <span className="label">E-Mail:</span>
              <span className="value">{userResult.email}</span>
            </div>

            <div className="admin-user-line">
              <span className="label">UID:</span>
              <span className="value mono">{userResult.uid}</span>
            </div>

            <div className="admin-badges">
              <span className={`badge ${isUserAdmin ? "on" : ""}`}>ADMIN</span>
              <span className={`badge ${isUserSuperAdmin ? "on" : ""}`}>SUPER-ADMIN</span>
            </div>

            <div className="admin-actions-row">
              <button
                className="green-button"
                disabled={loading}
                onClick={() =>
                  openConfirm({
                    title: "Admin-Rechte vergeben?",
                    text: "Möchtest du wirklich die Admin-Rechte vergeben?",
                    onOk: async () => {
                      closeConfirm();
                      await updateRole({ role: "admin", value: true });
                    },
                  })
                }
              >
                Admin-Rechte vergeben
              </button>

              <button
                className="cancel-btn"
                disabled={loading}
                onClick={() =>
                  openConfirm({
                    title: "Admin-Rechte wegnehmen?",
                    text: "Möchtest du wirklich die Admin-Rechte wegnehmen?",
                    danger: true,
                    onOk: async () => {
                      closeConfirm();
                      await updateRole({ role: "admin", value: false });
                    },
                  })
                }
              >
                Admin-Rechte wegnehmen
              </button>

              <button
                className="green-button"
                disabled={loading}
                onClick={() =>
                  openConfirm({
                    title: "Super-Admin-Rechte vergeben?",
                    text: "Möchtest du wirklich die Super-Admin-Rechte vergeben?",
                    onOk: async () => {
                      closeConfirm();
                      await updateRole({ role: "superadmin", value: true });
                    },
                  })
                }
              >
                Super-Admin-Rechte vergeben
              </button>

              <button
                className="cancel-btn"
                disabled={loading}
                onClick={() =>
                  openConfirm({
                    title: "Super-Admin-Rechte wegnehmen?",
                    text: "Möchtest du wirklich die Super-Admin-Rechte wegnehmen?",
                    danger: true,
                    onOk: async () => {
                      closeConfirm();
                      await updateRole({ role: "superadmin", value: false });
                    },
                  })
                }
              >
                Super-Admin-Rechte wegnehmen
              </button>
            </div>
          </div>
        )}
      </div>

      {toast.visible && <div className="admin-toast">{toast.text}</div>}

      {confirmModal.visible && (
        <div className="modal-overlay" onClick={closeConfirm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmModal.title}</h3>
            <p>{confirmModal.text}</p>

            <div className="modal-buttons">
              <button
                className={confirmModal.danger ? "delete-btn" : "green-button"}
                onClick={confirmModal.onOk}
              >
                {confirmModal.okText}
              </button>

              <button className="cancel-btn" onClick={closeConfirm}>
                {confirmModal.cancelText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;